// Utility functions for time formatting and conversion
// All time values are stored as INTEGER MINUTES in the database

export const formatHoursMinutes = (totalMinutes: number): string => {
  // Handle invalid input
  if (typeof totalMinutes !== 'number' || isNaN(totalMinutes)) {
    return '0h';
  }

  if (totalMinutes === 0) return '0h';

  // Handle negative values properly
  const isNegative = totalMinutes < 0;
  const absMinutes = Math.abs(totalMinutes);

  const hours = Math.floor(absMinutes / 60);
  const minutes = Math.round(absMinutes % 60);

  const prefix = isNegative ? '-' : '';

  if (hours === 0) {
    return `${prefix}${minutes}min`;
  } else if (minutes === 0) {
    return `${prefix}${hours}h`;
  } else {
    return `${prefix}${hours}h ${minutes}min`;
  }
};

export const parseHoursMinutes = (timeString: string): number => {
  // Parse formats like "1h 30min", "2h", "45min", "1,5", "1.5"
  // Returns MINUTES as integer
  if (!timeString || typeof timeString !== 'string') return 0;

  // Handle decimal input (1,5 or 1.5) - convert hours to minutes
  const decimalMatch = timeString.replace(',', '.').match(/^(\d+(?:\.\d+)?)$/);
  if (decimalMatch) {
    return Math.round(parseFloat(decimalMatch[1]) * 60);
  }

  // Handle hours and minutes format
  const hoursMatch = timeString.match(/(\d+)h/);
  const minutesMatch = timeString.match(/(\d+)min/);

  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

  return hours * 60 + minutes;
};

export const calculateHoursFromTimes = (startTime: string, endTime: string): number => {
  // Returns MINUTES as integer
  if (!startTime || !endTime || typeof startTime !== 'string' || typeof endTime !== 'string') return 0;

  let start = new Date(`2000-01-01T${startTime}:00`);
  let end = new Date(`2000-01-01T${endTime}:00`);

  // Check for invalid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

  // If start and end are exactly the same, return 0 (not 24h)
  if (startTime === endTime) {
    return 0;
  }

  // Handle overnight work (end time is next day)
  if (end < start) {
    end = new Date(`2000-01-02T${endTime}:00`);
  }

  const diffMs = end.getTime() - start.getTime();
  const minutes = Math.round(diffMs / (1000 * 60));
  return isNaN(minutes) ? 0 : minutes;
};

export const formatTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};